import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  AD_LICENSE_DECLINE_REASON_LABELS,
  AdLicenseDto,
  AdLicenseQuoteDto,
} from '../../../../common/general-types/ad-license.dto';
import { lastDeclinedLicense, resolveAdLicenseSlot } from '../../../../services/ad-license/ad-license-state';

/**
 * The Ad License area pinned to the bottom of a submitted-video card: Buy CTA, pending,
 * licensed, expired or locked, depending on the video and its latest licence.
 */
@Component({
  selector: 'vs-ad-license-slot',
  imports: [CurrencyPipe, DatePipe],
  templateUrl: './ad-license-slot.html',
  styleUrl: './ad-license-slot.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-y-2' },
})
export class AdLicenseSlot {
  readonly videoStatus = input.required<number>();
  /** Null hides the slot (licences can't be bought on this brief). */
  readonly quote = input<AdLicenseQuoteDto | null>(null);
  readonly license = input<AdLicenseDto | null | undefined>(null);

  readonly buy = output<void>();
  readonly cancel = output<void>();

  readonly slot = computed(() => resolveAdLicenseSlot(this.videoStatus(), this.quote(), this.license()));

  readonly currency = computed(() =>
    (this.license()?.currencyCode ?? this.quote()?.currencyCode ?? 'gbp').toUpperCase(),
  );

  /** Quote price in major units for the currency pipe. */
  readonly quotePrice = computed(() => (this.quote()?.brandPrice ?? 0) / 100);

  /** Price reserved for this licence, in major units. */
  readonly licensePrice = computed(() => (this.license()?.brandPrice ?? 0) / 100);

  readonly declinedReasonLabel = computed<string | null>(() => {
    const declined = lastDeclinedLicense(this.license());
    if (!declined) return null;
    return declined.declineReason ? AD_LICENSE_DECLINE_REASON_LABELS[declined.declineReason] : 'No reason given';
  });
}
